import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Home, ShoppingBasket, Wrench, Sofa, Receipt, Leaf, Shield,
  Flame, Users, ListChecks, LogOut, Menu, X, ChevronRight,
  Store, Car, PawPrint, Bell, CalendarDays, Wallet, ChefHat, Fuel, BookmarkPlus,
  Package, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import OnboardingTour from "@/components/OnboardingTour";
import TrialPill from "@/components/TrialPill";
import PaywallBanner from "@/components/PaywallBanner";
import useEdgeSwipe from "@/hooks/useEdgeSwipe";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/",          label: "Dashboard", icon: Home },
      { to: "/reminders", label: "Reminders", icon: Bell },
      { to: "/calendar",  label: "Calendar",  icon: CalendarDays },
    ],
  },
  {
    label: "Daily",
    items: [
      { to: "/grocery",     label: "Grocery",     icon: ShoppingBasket },
      { to: "/staples",     label: "Staples & Spices", icon: Package },
      { to: "/use-soon",    label: "Too good to waste", icon: ChefHat },
      { to: "/saved-recipes", label: "The Family Table", icon: BookmarkPlus },
      { to: "/chores",      label: "Chores",      icon: ListChecks },
      { to: "/vendors",     label: "Vendors",     icon: Store },
    ],
  },
  {
    label: "House",
    items: [
      { to: "/maintenance", label: "Maintenance", icon: Wrench },
      { to: "/wishlist",    label: "Wishlist",    icon: Sofa },
      { to: "/plants",      label: "Plants",      icon: Leaf },
      { to: "/gas-bottles", label: "Gas bottles", icon: Fuel },
    ],
  },
  {
    label: "Money & docs",
    items: [
      { to: "/bills",       label: "Bills",       icon: Flame },
      { to: "/expenses",    label: "Expenses",    icon: Wallet },
      { to: "/insurance",   label: "Insurance",   icon: Shield },
      { to: "/warranties",  label: "Warranties",  icon: Receipt },
      { to: "/providers",   label: "Providers",   icon: Users },
    ],
  },
  {
    label: "Family",
    items: [
      { to: "/vehicles",    label: "Vehicles",    icon: Car },
      { to: "/pets",        label: "Pets",        icon: PawPrint },
      { to: "/feedback",    label: "Send us a note", icon: MessageCircle },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // iOS-style left-edge swipe. On Dashboard it opens the menu drawer
  // (mirroring where the sidebar sits); everywhere else it acts as a
  // back button. Silent on non-touch devices.
  useEdgeSwipe({
    onSwipeRight: () => {
      if (location.pathname === "/") {
        setOpen(true);
      } else {
        nav(-1);
      }
    },
    enabled: !open,
  });

  const initials = (user?.name || "U").trim().split(" ").map((s) => s[0]).slice(0, 2).join("");

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-hearth-paper border-r border-hearth-line h-[100dvh] md:h-screen sticky top-0 flex flex-col">
      <div className="px-6 pt-7 pb-5 border-b border-hearth-line shrink-0">
        <NavLink
          to="/"
          data-testid="sidebar-logo-home"
          onClick={() => setOpen(false)}
          className="inline-block hover:opacity-80 transition-opacity"
          aria-label="Back to dashboard"
        >
          <Logo size={36} showWord />
        </NavLink>
        <div className="caption mt-2 pl-[46px]">a thoughtful home</div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-4 last:mb-0">
            <p className="caption px-4 mb-1.5">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={`nav-${it.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-lg px-4 py-2 text-sm transition ${
                        isActive
                          ? "bg-hearth-ink text-white"
                          : "text-hearth-mute hover:bg-hearth-stone hover:text-hearth-ink"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon size={16} strokeWidth={1.6}
                              className={isActive ? "text-hearth-claySoft" : ""} />
                        <span className="flex-1">{it.label}</span>
                        {isActive && <ChevronRight size={13} className="text-white/60" />}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-hearth-line px-4 py-4 space-y-2 shrink-0">
        <TrialPill />
        <NavLink
          to="/profile"
          data-testid="nav-profile"
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2 transition ${
              isActive ? "bg-hearth-stone" : "hover:bg-hearth-stone"
            }`
          }
        >
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-hearth-slate text-white grid place-items-center font-medium text-sm shrink-0">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url.startsWith("http") ? user.avatar_url : (process.env.REACT_APP_BACKEND_URL || "") + user.avatar_url}
                alt={user?.name || "avatar"}
                className="w-full h-full object-cover"
              />
            ) : (initials || "U")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-hearth-ink truncate">{user?.name}</div>
            <div className="text-xs text-hearth-mute truncate">{user?.email}</div>
          </div>
        </NavLink>
        <Button
          data-testid="logout-button"
          variant="ghost"
          onClick={async () => { await logout(); nav("/login"); }}
          className="w-full justify-start rounded-lg text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10 h-9"
        >
          <LogOut size={15} className="mr-2" /> Sign out
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-hearth-cream grain">
      <div className="flex">
        <div className="hidden md:block">{Sidebar}</div>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-hearth-ink/30" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <PaywallBanner />
          <header className="glass sticky top-0 z-20 border-b border-hearth-line/80">
            <div className="flex items-center justify-between px-6 md:px-10 py-3">
              <button
                data-testid="mobile-menu-toggle"
                className="md:hidden p-2 rounded-lg hover:bg-hearth-stone"
                onClick={() => setOpen(!open)}
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
              <div className="hidden md:flex items-baseline gap-2">
                <span className="caption">{greeting()}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="header-user-firstname"
                  onClick={() => nav("/profile")}
                  className="hidden sm:inline-flex items-center gap-1 rounded-full px-3 h-9 text-sm text-hearth-ink hover:bg-hearth-stone transition"
                  aria-label="Go to profile & admin"
                >
                  <span className="font-display italic text-base">
                    {user?.name?.split(" ")[0] || "friend"}
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="header-avatar"
                  onClick={() => nav("/profile")}
                  className="w-9 h-9 rounded-lg overflow-hidden bg-hearth-ink text-white grid place-items-center text-sm font-medium hover:opacity-90 transition"
                  aria-label="Go to profile & admin"
                >
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url.startsWith("http") ? user.avatar_url : (process.env.REACT_APP_BACKEND_URL || "") + user.avatar_url}
                      alt={user?.name || "avatar"}
                      className="w-full h-full object-cover"
                    />
                  ) : (initials || "U")}
                </button>
              </div>
            </div>
          </header>

          <div className="p-6 md:p-10 lg:p-12">
            <Outlet />
          </div>
        </main>
      </div>
      {/* First Light — one-time onboarding tour for new signups.
          Auto-fires when user.onboarding_completed === false. */}
      <OnboardingTour />
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Still up";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}

